import { useState, useEffect, useMemo } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Calculator, Users, DollarSign, Settings, Save, RefreshCw, Trash2,
  ChevronRight, Sparkles, Plus, X, FileDown, Pencil,
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";
import { LOGO_OBRAPADRAO_BASE64 } from "@/lib/logo-base64";
import { BRAND } from "@/lib/brand";
import html2pdf from "html2pdf.js";

// Detecta a pasta padrão de downloads conforme o sistema operacional
const getDownloadFolderHint = () => {
  const ua = navigator.userAgent || "";
  if (/Macintosh|Mac OS X/i.test(ua)) return "~/Downloads (Finder › Downloads)";
  if (/Windows/i.test(ua)) return "C:\\Users\\<seu-usuário>\\Downloads";
  if (/Linux/i.test(ua)) return "~/Downloads (ou pasta configurada no navegador)";
  if (/iPhone|iPad|iPod/i.test(ua)) return "App Arquivos › Downloads";
  if (/Android/i.test(ua)) return "Downloads do dispositivo";
  return "pasta de Downloads padrão do seu navegador";
};

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtPct = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 2 }).format(v || 0);

const emptyEquipeItem = () => ({ cargo_id: "", quantidade: 1 });

const emptyMaterialLinha = () => ({ material_id: "", quantidade: 1 });

const emptyForm = () => ({
  nome_cliente: "",
  numero_proposta: "",
  descricao: "",
  dias: 12,
  equipe_cargos: [emptyEquipeItem()],
  custo_material: 0,
  materiais_lista: [],        // [{material_id, quantidade}]
  multiplicador_mo: 2.0,
  multiplicador_material: 2.5,
  multiplicador_custo_fixo: 1.5,
  num_obras: 4,
});

export default function PrecificacaoPage() {
  const [config, setConfig] = useState(null);
  const [cargos, setCargos] = useState([]);
  const [calc, setCalc] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [orcamentos, setOrcamentos] = useState([]);
  const [materiaisEstoque, setMateriaisEstoque] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState("calcular");
  const [searchHistorico, setSearchHistorico] = useState("");
  const [viewItem, setViewItem] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [pdfModal, setPdfModal] = useState({ open: false, blobUrl: null, filename: null });

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${API}/precificacao/config`);
      setConfig(r.data);
      if (r.data?.obras_simultaneas && form.num_obras === 4) {
        setForm((f) => ({ ...f, num_obras: r.data.obras_simultaneas }));
      }
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao carregar config"));
    }
  };

  const loadCargos = async () => {
    try {
      const r = await axios.get(`${API}/cargos`);
      // ordena por nivel desc, depois nome
      const list = (r.data || []).slice().sort((a, b) => (b.nivel || 0) - (a.nivel || 0));
      setCargos(list);
    } catch (e) {
      console.warn('[Precificacao] Falha ao carregar cargos:', e?.message || e);
    }
  };

  const loadOrcamentos = async () => {
    try {
      const r = await axios.get(`${API}/precificacao/orcamentos`);
      setOrcamentos(r.data || []);
    } catch (e) {
      console.warn('[Precificacao] Falha ao carregar orcamentos:', e?.message || e);
    }
  };

  const loadMateriaisEstoque = async () => {
    try {
      const r = await axios.get(`${API}/estoque`);
      setMateriaisEstoque(r.data || []);
    } catch (e) {
      console.warn('[Precificacao] Falha ao carregar materiais de estoque:', e?.message || e);
    }
  };

  const seedCargos = async () => {
    try {
      await axios.post(`${API}/precificacao/seed-cargos-padrao`);
      await loadCargos();
      toast.success("Cargos padrão (LIDER/N2/N1/AUX) prontos para uso");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao criar cargos padrão"));
    }
  };

  useEffect(() => {
    loadConfig();
    loadCargos();
    loadOrcamentos();
    loadMateriaisEstoque();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== Cargos disponíveis (com custo_diario) ======
  const cargosComCusto = useMemo(
    () => cargos.filter((c) => Number(c.custo_diario) > 0),
    [cargos]
  );

  // ====== Equipe helpers ======
  const addCargoLinha = () =>
    setForm((f) => ({ ...f, equipe_cargos: [...f.equipe_cargos, emptyEquipeItem()] }));
  const removeCargoLinha = (i) =>
    setForm((f) => ({ ...f, equipe_cargos: f.equipe_cargos.filter((_, idx) => idx !== i) }));
  const setCargoLinha = (i, patch) =>
    setForm((f) => ({
      ...f,
      equipe_cargos: f.equipe_cargos.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));

  const resumoEquipe = useMemo(() => {
    let totalPessoas = 0;
    let custoDia = 0;
    for (const item of form.equipe_cargos) {
      const c = cargosComCusto.find((cc) => cc.id === item.cargo_id);
      const qtd = parseInt(item.quantidade) || 0;
      const cd = c ? Number(c.custo_diario) : 0;
      totalPessoas += qtd;
      custoDia += qtd * cd;
    }
    return { totalPessoas, custoDia };
  }, [form.equipe_cargos, cargosComCusto]);

  // Subtotal computado da lista de materiais
  const materiaisSubtotal = useMemo(() => {
    return (form.materiais_lista || []).reduce((sum, l) => {
      const m = materiaisEstoque.find((x) => x.id === l.material_id);
      const preco = parseFloat(m?.valor_unitario) || 0;
      const qtd = parseFloat(l.quantidade) || 0;
      return sum + preco * qtd;
    }, 0);
  }, [form.materiais_lista, materiaisEstoque]);

  // ====== Validações ======
  const validarPayload = () => {
    const erros = [];
    if (!form.nome_cliente.trim()) erros.push("Nome do Cliente é obrigatório");
    if (!form.numero_proposta.trim()) erros.push("Número da Proposta é obrigatório");
    if (!form.dias || form.dias <= 0) erros.push("Duração da Obra deve ser > 0");
    const equipeValida = form.equipe_cargos.filter((e) => e.cargo_id && (parseInt(e.quantidade) || 0) > 0);
    if (equipeValida.length === 0) erros.push("Adicione pelo menos 1 cargo com quantidade > 0");
    // Materiais: ou usa lista de itens, ou usa custo único
    const usandoLista = (form.materiais_lista || []).some((l) => l.material_id && parseFloat(l.quantidade) > 0);
    if (!usandoLista && (parseFloat(form.custo_material) || 0) < 0) erros.push("Custo Material não pode ser negativo");
    if (usandoLista) {
      for (const l of form.materiais_lista) {
        if (l.material_id && (!l.quantidade || parseFloat(l.quantidade) <= 0)) {
          erros.push("Informe a quantidade para todos os materiais selecionados");
          break;
        }
      }
    }
    for (const [k, l] of [["multiplicador_mo","MO"], ["multiplicador_material","Material"], ["multiplicador_custo_fixo","Custo Fixo"]]) {
      const v = parseFloat(form[k]);
      if (!(v >= 1.0 && v <= 5.0)) erros.push(`Multiplicador ${l} deve estar entre 1,0 e 5,0`);
    }
    if ((parseInt(form.num_obras) || 0) < 1) erros.push("Número de Obras Simultâneas deve ser >= 1");
    return erros;
  };

  const buildPayload = () => {
    // Lista de materiais válidos (com material_id e quantidade > 0)
    const materiaisValidos = (form.materiais_lista || [])
      .filter((l) => l.material_id && parseFloat(l.quantidade) > 0)
      .map((l) => ({
        material_id: l.material_id,
        quantidade: parseFloat(l.quantidade) || 0,
      }));

    const payload = {
      nome_cliente: form.nome_cliente.trim(),
      numero_proposta: form.numero_proposta.trim(),
      titulo: form.numero_proposta.trim() + " - " + form.nome_cliente.trim(),
      descricao: form.descricao,
      dias: parseInt(form.dias) || 0,
      equipe_cargos: form.equipe_cargos
        .filter((e) => e.cargo_id && (parseInt(e.quantidade) || 0) > 0)
        .map((e) => ({ cargo_id: e.cargo_id, quantidade: parseInt(e.quantidade) || 0 })),
      multiplicador_mo: parseFloat(form.multiplicador_mo) || 1.0,
      multiplicador_material: parseFloat(form.multiplicador_material) || 1.0,
      multiplicador_custo_fixo: parseFloat(form.multiplicador_custo_fixo) || 1.0,
      num_obras: parseInt(form.num_obras) || 1,
    };

    if (materiaisValidos.length > 0) {
      payload.materiais = materiaisValidos;
    } else {
      payload.custo_material = parseFloat(form.custo_material) || 0;
    }
    return payload;
  };

  const calcular = async () => {
    const erros = validarPayload();
    if (erros.length) { toast.error(erros[0]); return; }
    setCalculando(true);
    try {
      const r = await axios.post(`${API}/precificacao/calcular`, buildPayload());
      setCalc(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao calcular"));
      setCalc(null);
    } finally {
      setCalculando(false);
    }
  };

  const salvar = async () => {
    const erros = validarPayload();
    if (erros.length) { toast.error(erros[0]); return; }
    setSalvando(true);
    try {
      if (editingId) {
        await axios.put(`${API}/precificacao/orcamentos/${editingId}`, buildPayload());
        toast.success("Orçamento atualizado!");
      } else {
        await axios.post(`${API}/precificacao/salvar`, buildPayload());
        toast.success("Orçamento salvo!");
      }
      await loadOrcamentos();
      novaPrecificacao();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao salvar"));
    } finally {
      setSalvando(false);
    }
  };

  const editar = async (id) => {
    try {
      const r = await axios.get(`${API}/precificacao/orcamentos/${id}`);
      const i = r.data?.input || {};
      setForm({
        nome_cliente: r.data.nome_cliente || "",
        numero_proposta: r.data.numero_proposta || "",
        descricao: i.descricao || "",
        dias: i.dias || 12,
        equipe_cargos: (i.equipe_cargos || []).map((e) => ({
          cargo_id: e.cargo_id || "", quantidade: e.quantidade || 0,
        })),
        custo_material: i.custo_material || 0,
        materiais_lista: (i.materiais || []).map((m) => ({
          material_id: m.material_id || "",
          quantidade: m.quantidade || 0,
        })),
        multiplicador_mo: i.multiplicador_mo || 2.0,
        multiplicador_material: i.multiplicador_material || 2.5,
        multiplicador_custo_fixo: i.multiplicador_custo_fixo || 1.5,
        num_obras: i.num_obras || 4,
      });
      setCalc(r.data?.result || null);
      setEditingId(id);
      setTab("calcular");  // navega imediatamente para Calcular
      toast.info("Editando orçamento #" + (r.data?.numero ?? ""));
      // Rola para o topo
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao carregar"));
    }
  };

  const visualizar = async (id) => {
    try {
      const r = await axios.get(`${API}/precificacao/orcamentos/${id}`);
      setViewItem(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao carregar"));
    }
  };

  const removerOrcamento = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta precificação?")) return;
    try {
      await axios.delete(`${API}/precificacao/orcamentos/${id}`);
      toast.success("Removido");
      if (editingId === id) novaPrecificacao();
      loadOrcamentos();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao remover"));
    }
  };

  const novaPrecificacao = () => {
    setForm(emptyForm());
    setCalc(null);
    setEditingId(null);
  };

  // ====== PDF — abre o PDF no navegador (visualizador nativo) e oferece download direto ======
  // Estratégia: gera Blob real do PDF e mostra um diálogo com botões "Abrir" e "Baixar",
  // ambos clicados diretamente pelo usuário (user-gesture), o que contorna restrições
  // de pop-up e do sandbox de iframe (preview do Emergent, Chrome corporativo etc).
  const gerarPDF = async (sourceCalc, sourceForm) => {
    const c = sourceCalc || calc;
    const f = sourceForm || form;
    if (!c) { toast.error("Calcule a precificação antes de gerar o PDF"); return; }

    const cabec = c.cabecalho || {};
    const nomeCliente = cabec.nome_cliente || f.nome_cliente || "Cliente";
    const numProposta = cabec.numero_proposta || f.numero_proposta || "—";
    const dataEmissao = new Date().toLocaleDateString("pt-BR");

    const linhasEquipe = c.custos.cmo_breakdown.map((e) => `
      <tr>
        <td>${e.nome || "—"}</td>
        <td class="c">${e.quantidade}</td>
        <td class="r mono">${fmtBRL(e.custo_diario)}</td>
        <td class="r mono">${fmtBRL(e.subtotal_dia)}</td>
      </tr>`).join("");

    // Lista detalhada de materiais (se houver no resultado)
    const materiaisDetalhe = (c.custos?.materiais_lista || []);

    // Percentuais — recalcula a partir dos valores se faltarem no snapshot (compat retroativo)
    const pv = c.venda.preco_venda || 0;
    const pctMo  = c.venda.pct_mo_sobre_venda       ?? (pv > 0 ? (c.venda.mo_com_markup       / pv * 100) : 0);
    const pctCf  = c.venda.pct_cf_sobre_venda       ?? (pv > 0 ? (c.venda.cf_com_markup       / pv * 100) : 0);
    const pctMat = c.venda.pct_material_sobre_venda ?? (pv > 0 ? (c.venda.material_com_markup / pv * 100) : 0);
    const blocoMateriais = materiaisDetalhe.length === 0 ? "" : `
    <h2>Materiais utilizados</h2>
    <table class="tbl-mat">
      <colgroup><col class="c1"><col class="c2"><col class="c3"><col class="c4"><col class="c5"></colgroup>
      <thead><tr><th>Código</th><th>Material</th><th class="c">Qtd</th><th class="r">Preço Unit.</th><th class="r">Subtotal</th></tr></thead>
      <tbody>
        ${materiaisDetalhe.map((m) => `
          <tr>
            <td class="mono">${m.codigo || "—"}</td>
            <td>${m.nome || "—"}</td>
            <td class="c mono">${m.quantidade}${m.unidade ? " " + m.unidade : ""}</td>
            <td class="r mono">${fmtBRL(m.valor_unitario)}</td>
            <td class="r mono">${fmtBRL(m.valor_total)}</td>
          </tr>`).join("")}
        <tr style="background:#f1f5f9;font-weight:700">
          <td colspan="4" class="r">Total de materiais</td>
          <td class="r mono">${fmtBRL(c.custos.custo_material)}</td>
        </tr>
      </tbody>
    </table>`;

    const linhasOpcoes = c.opcoes_pagamento.map((op) => `
      <tr ${op.com_juros ? 'class="bg-amber"' : ''}>
        <td class="c">${op.id}</td>
        <td>${op.label}${op.com_juros ? ' <span class="badge">juros</span>' : ''}</td>
        <td class="r mono">${fmtBRL(op.valor_entrada)}</td>
        <td class="r mono">${op.num_parcelas > 0 ? `${op.num_parcelas}× ${fmtBRL(op.valor_parcela)}` : '—'}</td>
        <td class="r mono">${fmtBRL(op.total_pagar)}</td>
      </tr>`).join("");

    const htmlInner = `
<style>
  * { box-sizing: border-box; }
  .pdf-root {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #0f172a;
    width: 760px;        /* A4 (794px@96dpi) menos folga lateral para evitar corte */
    padding: 0;
    background: #ffffff;
    position: relative;
  }

  /* ---- Marca d'água ---- */
  .pdf-root .watermark {
    position: absolute;
    top: 38%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-28deg);
    font-size: 140px;
    font-weight: 900;
    letter-spacing: 14px;
    color: rgba(10, 22, 40, 0.07);
    pointer-events: none;
    z-index: 0;
    white-space: nowrap;
  }
  .pdf-root .page { position: relative; z-index: 1; padding: 0 4px; }

  /* ---- Cabeçalho Obra Padrão ---- */
  .pdf-root .brand-bar { display: flex; align-items: stretch; height: 6px; margin-bottom: 14px; }
  .pdf-root .brand-bar .b1 { flex: 3; background: #0A1628; }
  .pdf-root .brand-bar .b2 { flex: 1; background: #D4A437; }

  .pdf-root .brand-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 0 4px 12px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px;
  }
  .pdf-root .brand-logo { display: flex; align-items: center; gap: 12px; }
  .pdf-root .brand-logo img {
    width: 56px; height: 56px; border-radius: 8px; object-fit: cover;
    box-shadow: 0 2px 8px rgba(10,22,40,0.25);
    border: 1px solid #e2e8f0;
  }
  .pdf-root .brand-logo .name { line-height: 1.05; }
  .pdf-root .brand-logo .name .t1 { font-size: 20px; font-weight: 900; color: #0A1628; letter-spacing: 0.5px; }
  .pdf-root .brand-logo .name .t1 span { color: #D4A437; }
  .pdf-root .brand-logo .name .t2 { font-size: 9px; color: #64748b; letter-spacing: 1.8px; text-transform: uppercase; margin-top: 4px; font-weight: 600; }
  .pdf-root .doc-meta { text-align: right; font-size: 10px; color: #475569; }
  .pdf-root .doc-meta .badge {
    display: inline-block; background: #0A1628; color: #fff; padding: 4px 10px;
    border-radius: 999px; font-size: 9px; font-weight: 700; letter-spacing: 1px;
  }

  /* ---- Título da proposta ---- */
  .pdf-root .title-block { padding: 0 4px 10px; margin-bottom: 16px; border-bottom: 2px solid #0A1628; position: relative; }
  .pdf-root .title-block::after {
    content: ""; position: absolute; left: 0; bottom: -2px; width: 80px; height: 2px; background: #D4A437;
  }
  .pdf-root .title-block .kicker {
    font-size: 9px; font-weight: 700; letter-spacing: 2px; color: #D4A437; text-transform: uppercase;
  }
  .pdf-root .title-block h1 { font-size: 24px; margin: 4px 0 8px; color: #0A1628; font-weight: 800; }
  .pdf-root .title-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 11px; color: #334155;
  }
  .pdf-root .title-grid b { color: #0A1628; }

  /* ---- Seções ---- */
  .pdf-root h2 {
    font-size: 11px; margin: 18px 0 8px; text-transform: uppercase;
    color: #0A1628; letter-spacing: 1.2px; font-weight: 800;
    padding: 4px 8px; background: linear-gradient(90deg, #f8fafc 0%, transparent 100%);
    border-left: 3px solid #D4A437;
  }

  /* ---- Info grid (Composição) ---- */
  .pdf-root .info-grid {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; font-size: 10.5px; margin: 8px 4px 12px;
  }
  .pdf-root .info-grid div {
    background: #f8fafc; padding: 8px 10px; border-radius: 6px; border: 1px solid #e2e8f0;
  }
  .pdf-root .info-grid b {
    display: block; font-size: 8.5px; color: #64748b; text-transform: uppercase; margin-bottom: 3px; letter-spacing: 0.5px; font-weight: 700;
  }
  .pdf-root .info-grid .val { font-weight: 700; color: #0A1628; font-size: 11px; }

  /* ---- Tabelas ---- */
  .pdf-root table {
    width: 100%; border-collapse: collapse; font-size: 10.5px;
    table-layout: fixed;          /* essencial: respeita larguras das colunas */
    margin: 0 4px 4px; width: calc(100% - 8px);
    page-break-inside: auto;      /* permite quebra entre linhas */
  }
  .pdf-root tr { page-break-inside: avoid; page-break-after: auto; }
  .pdf-root thead { display: table-header-group; }  /* cabecalho repete em paginas seguintes */
  .pdf-root tfoot { display: table-footer-group; }
  .pdf-root h2 { page-break-after: avoid; }         /* nao deixar titulo orfao */
  .pdf-root .footer { page-break-inside: avoid; }
  .pdf-root th, .pdf-root td {
    border: 1px solid #e2e8f0; padding: 6px 8px;
    word-wrap: break-word; overflow-wrap: break-word;
  }
  .pdf-root th {
    background: #0A1628; color: #ffffff; text-align: left; font-weight: 600;
    font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.4px;
  }
  .pdf-root td.c { text-align: center; }
  .pdf-root td.r { text-align: right; }
  .pdf-root tbody tr:nth-child(even) { background: #f8fafc; }
  .pdf-root .mono { font-family: 'SF Mono', Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
  .pdf-root .price td { background: #0A1628 !important; color: #ffffff; font-weight: 800; font-size: 12px; padding: 9px 8px; }
  .pdf-root .price td.r { color: #D4A437; }
  .pdf-root .bg-amber { background: #fefce8 !important; }
  .pdf-root .badge {
    display:inline-block; padding: 1px 6px; background: #fde68a; border-radius: 3px;
    font-size: 8.5px; color: #78350f; font-weight: 700; margin-left: 4px;
  }

  /* Larguras explícitas das colunas (table-layout: fixed) */
  .pdf-root table.tbl-equipe col.c1 { width: 46%; }
  .pdf-root table.tbl-equipe col.c2 { width: 12%; }
  .pdf-root table.tbl-equipe col.c3 { width: 21%; }
  .pdf-root table.tbl-equipe col.c4 { width: 21%; }

  .pdf-root table.tbl-custos col.c1 { width: 36%; }
  .pdf-root table.tbl-custos col.c2 { width: 22%; }
  .pdf-root table.tbl-custos col.c3 { width: 18%; }
  .pdf-root table.tbl-custos col.c4 { width: 24%; }

  .pdf-root table.tbl-pag col.c1 { width: 6%; }
  .pdf-root table.tbl-pag col.c2 { width: 42%; }
  .pdf-root table.tbl-pag col.c3 { width: 16%; }
  .pdf-root table.tbl-pag col.c4 { width: 18%; }
  .pdf-root table.tbl-pag col.c5 { width: 18%; }

  .pdf-root table.tbl-mat col.c1 { width: 12%; }
  .pdf-root table.tbl-mat col.c2 { width: 42%; }
  .pdf-root table.tbl-mat col.c3 { width: 12%; }
  .pdf-root table.tbl-mat col.c4 { width: 17%; }
  .pdf-root table.tbl-mat col.c5 { width: 17%; }

  /* Visualização de barras percentuais */
  .pdf-root table.tbl-pct .bar { height: 12px; width: 100%; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
  .pdf-root table.tbl-pct .bar-fill { height: 100%; display: block; }
  .pdf-root table.tbl-pct .bar-mo  { background: #2563eb; }
  .pdf-root table.tbl-pct .bar-cf  { background: #d97706; }
  .pdf-root table.tbl-pct .bar-mat { background: #059669; }

  /* ---- Rodapé ---- */
  .pdf-root .footer {
    margin-top: 22px; padding: 12px 4px 0;
    border-top: 1px solid #e2e8f0;
    display: flex; justify-content: space-between; align-items: center;
    font-size: 9px; color: #64748b;
  }
  .pdf-root .footer .left b { color: #0A1628; font-size: 10px; }
  .pdf-root .footer .right { text-align: right; }
  .pdf-root .footer .dot { color: #D4A437; margin: 0 5px; }
</style>
<div class="pdf-root">
  <div class="watermark">PROPOSTA</div>
  <div class="page">

    <div class="brand-bar"><div class="b1"></div><div class="b2"></div></div>

    <div class="brand-header">
      <div class="brand-logo">
        <img src="${LOGO_OBRAPADRAO_BASE64}" alt="Obra Padrão" />
        <div class="name">
          <div class="t1">Obra <span>Padrão</span></div>
          <div class="t2">Sistema de Gestão de Obras</div>
        </div>
      </div>
      <div class="doc-meta">
        <div class="badge">PROPOSTA COMERCIAL</div>
        <div style="margin-top:6px"><b>Emissão:</b> ${dataEmissao}</div>
        <div><b>Nº Proposta:</b> ${numProposta}</div>
      </div>
    </div>

    <div class="title-block">
      <div class="kicker">Reforma de Fachada · Serviço Completo</div>
      <h1>${nomeCliente}</h1>
      <div class="title-grid">
        <div><b>Cliente:</b> ${nomeCliente}</div>
        <div><b>Duração estimada:</b> ${c.inputs.dias} dias úteis</div>
      </div>
    </div>

    <h2>Equipe alocada na obra</h2>
    <table class="tbl-equipe">
      <colgroup><col class="c1"><col class="c2"><col class="c3"><col class="c4"></colgroup>
      <thead><tr><th>Cargo</th><th class="c">Qtd</th><th class="r">Custo Diário</th><th class="r">Subtotal/dia</th></tr></thead>
      <tbody>${linhasEquipe}</tbody>
    </table>

    <h2>Composição de custos</h2>
    <div class="info-grid">
      <div><b>Duração</b><span class="val">${c.inputs.dias} dias</span></div>
      <div><b>Obras simult.</b><span class="val">${c.inputs.num_obras}</span></div>
      <div><b>Custo fixo / mês</b><span class="val">${fmtBRL(c.inputs.custo_fixo_mensal)}</span></div>
      <div><b>Dias úteis / mês</b><span class="val">${c.inputs.dias_uteis_mes}</span></div>
    </div>
    <table class="tbl-custos">
      <colgroup><col class="c1"><col class="c2"><col class="c3"><col class="c4"></colgroup>
      <thead><tr><th>Item</th><th class="r">Custo base</th><th class="c">Multiplicador</th><th class="r">Total c/ markup</th></tr></thead>
      <tbody>
        <tr><td>Mão de Obra (CMO)</td><td class="r mono">${fmtBRL(c.custos.cmo_total)}</td><td class="c">× ${c.inputs.multiplicador_mo}</td><td class="r mono">${fmtBRL(c.venda.mo_com_markup)}</td></tr>
        <tr><td>Custo Fixo Mensal</td><td class="r mono">${fmtBRL(c.custos.cf_total)}</td><td class="c">× ${c.inputs.multiplicador_custo_fixo}</td><td class="r mono">${fmtBRL(c.venda.cf_com_markup)}</td></tr>
        <tr><td>Material</td><td class="r mono">${fmtBRL(c.custos.custo_material)}</td><td class="c">× ${c.inputs.multiplicador_material}</td><td class="r mono">${fmtBRL(c.venda.material_com_markup)}</td></tr>
        <tr class="price"><td>PREÇO DE VENDA</td><td class="r mono">${fmtBRL(c.custos.custo_base)}</td><td></td><td class="r mono">${fmtBRL(c.venda.preco_venda)}</td></tr>
      </tbody>
    </table>

    <!-- Composição percentual sobre o preço final -->
    <h2>Composição do preço final</h2>
    <table class="tbl-pct">
      <colgroup><col style="width:34%"><col style="width:22%"><col style="width:22%"><col style="width:22%"></colgroup>
      <thead><tr><th>Item</th><th class="r">Valor</th><th class="c">% sobre venda</th><th>Visualização</th></tr></thead>
      <tbody>
        <tr>
          <td>Mão de Obra</td>
          <td class="r mono">${fmtBRL(c.venda.mo_com_markup)}</td>
          <td class="c mono"><b>${pctMo.toFixed(1)}%</b></td>
          <td><div class="bar"><span class="bar-fill bar-mo" style="width:${pctMo.toFixed(2)}%"></span></div></td>
        </tr>
        <tr>
          <td>Custo Fixo</td>
          <td class="r mono">${fmtBRL(c.venda.cf_com_markup)}</td>
          <td class="c mono"><b>${pctCf.toFixed(1)}%</b></td>
          <td><div class="bar"><span class="bar-fill bar-cf" style="width:${pctCf.toFixed(2)}%"></span></div></td>
        </tr>
        <tr>
          <td>Material</td>
          <td class="r mono">${fmtBRL(c.venda.material_com_markup)}</td>
          <td class="c mono"><b>${pctMat.toFixed(1)}%</b></td>
          <td><div class="bar"><span class="bar-fill bar-mat" style="width:${pctMat.toFixed(2)}%"></span></div></td>
        </tr>
        <tr style="background:#0A1628;color:#D4A437;font-weight:700">
          <td>Total</td>
          <td class="r mono">${fmtBRL(c.venda.preco_venda)}</td>
          <td class="c mono">100%</td>
          <td></td>
        </tr>
      </tbody>
    </table>
    ${blocoMateriais}

    <h2>Opções de pagamento — taxa ${(c.inputs.taxa_juros_mensal * 100).toFixed(2)}% a.m. quando aplicável</h2>
    <table class="tbl-pag">
      <colgroup><col class="c1"><col class="c2"><col class="c3"><col class="c4"><col class="c5"></colgroup>
      <thead><tr><th class="c">#</th><th>Forma de pagamento</th><th class="r">Entrada</th><th class="r">Parcelas</th><th class="r">Total</th></tr></thead>
      <tbody>${linhasOpcoes}</tbody>
    </table>

    <div class="footer">
      <div class="left">
        <b>${BRAND.fullName}</b><br>
        Proposta válida por 15 dias a partir da data de emissão.
      </div>
      <div class="right">
        Documento gerado eletronicamente<span class="dot">●</span>${dataEmissao}<br>
        Proposta nº ${numProposta}
      </div>
    </div>

  </div>
</div>`;

    // Nome do arquivo seguro
    const safe = (s) => String(s || "").replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40);
    const filename = `Proposta_${safe(numProposta)}_${safe(nomeCliente)}.pdf`;

    // Container offscreen para renderização do html2canvas
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-10000px";
    container.style.top = "0";
    container.style.background = "#fff";
    container.innerHTML = htmlInner;
    document.body.appendChild(container);

    const toastId = toast.loading("Gerando PDF…");
    try {
      const worker = html2pdf()
        .from(container.querySelector(".pdf-root"))
        .set({
          margin: [12, 12, 18, 12],   // top, right, bottom (18mm para folga), left
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            width: 760,
            windowWidth: 760,
            scrollX: 0,
            scrollY: 0,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait", compress: true },
          // CSS controla onde quebrar; cada <tr> recebe page-break-inside: avoid
          // para nao cortar uma opcao no meio.
          pagebreak: { mode: ["css", "legacy"], avoid: "tr" },
        });

      // Gera o blob do PDF (não dispara nenhum download automático)
      const pdfBlob = await worker.outputPdf("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);

      // Limpa estado anterior (revoga URL pendente, se houver)
      setPdfModal((prev) => {
        if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
        return { open: true, blobUrl, filename };
      });

      toast.success("PDF pronto — visualize ou baixe na janela aberta.", { id: toastId, duration: 4000 });
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
      toast.error("Falha ao gerar PDF. Tente novamente.", { id: toastId });
    } finally {
      document.body.removeChild(container);
    }
  };

  const closePdfModal = () => {
    setPdfModal((prev) => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return { open: false, blobUrl: null, filename: null };
    });
  };

  const naoTemCargosComCusto = cargosComCusto.length === 0;

  return (
    <MainLayout>
      <div className="space-y-6 print:space-y-3" data-testid="page-precificacao">
        {/* Header */}
        <div className="flex items-center justify-between print:hidden">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Calculator className="h-7 w-7 text-luvcorp-navy" />
              Precificação de Obras
              {editingId && (
                <span className="ml-2 text-xs font-normal px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300">
                  Editando
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              CMO + Custo Fixo + Material com multiplicadores configuráveis e 8 opções de pagamento.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={novaPrecificacao} data-testid="btn-nova">
              <Plus className="h-4 w-4 mr-2" /> Nova
            </Button>
            <Button variant="outline" onClick={() => setShowConfig(true)} data-testid="btn-abrir-config">
              <Settings className="h-4 w-4 mr-2" /> Configurações
            </Button>
          </div>
        </div>

        {/* Aviso: sem cargos cadastrados com custo_diario */}
        {naoTemCargosComCusto && (
          <Card className="border-amber-300 bg-amber-50 print:hidden">
            <CardContent className="p-4 flex items-start gap-3">
              <Users className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">Nenhum cargo com custo diário cadastrado</p>
                <p className="text-xs text-amber-800 mt-1">
                  O módulo precisa de cargos com <b>Custo Diário</b> definido. Clique abaixo para criar os 4 cargos padrão
                  (LIDER R$400 · N2 R$245 · N1 R$215 · AUX R$180) — você poderá editar depois em <b>RH &gt; Cargos</b>.
                </p>
                <Button size="sm" className="mt-2 bg-amber-700 hover:bg-amber-800 text-white" onClick={seedCargos}>
                  Criar Cargos Padrão
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={tab} onValueChange={setTab} className="space-y-4 print:hidden">
          <TabsList>
            <TabsTrigger value="calcular">
              <Calculator className="h-4 w-4 mr-2" /> Calcular
            </TabsTrigger>
            <TabsTrigger value="historico">
              <Sparkles className="h-4 w-4 mr-2" /> Histórico ({orcamentos.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calcular">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ============= INPUT ============= */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">1. Cabeçalho da Proposta</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome do Cliente *</Label>
                      <Input
                        value={form.nome_cliente}
                        onChange={(e) => setForm({ ...form, nome_cliente: e.target.value })}
                        placeholder="Ex: Condomínio Residencial Bela Vista"
                        data-testid="input-nome-cliente"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Número da Proposta *</Label>
                      <Input
                        value={form.numero_proposta}
                        onChange={(e) => setForm({ ...form, numero_proposta: e.target.value })}
                        placeholder="Ex: PROP-2026-001"
                        data-testid="input-num-proposta"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Duração (dias) *</Label>
                      <Input
                        type="number" min="1"
                        value={form.dias}
                        onChange={(e) => setForm({ ...form, dias: e.target.value })}
                        data-testid="input-dias"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Obras Simultâneas *</Label>
                      <Input
                        type="number" min="1"
                        value={form.num_obras}
                        onChange={(e) => setForm({ ...form, num_obras: e.target.value })}
                        data-testid="input-num-obras"
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t">
                    <CardTitle className="text-base mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" /> 2. Equipe da Obra
                    </CardTitle>
                    <div className="space-y-2" data-testid="lista-equipe">
                      {form.equipe_cargos.map((it, i) => {
                        const c = cargosComCusto.find((cc) => cc.id === it.cargo_id);
                        return (
                          <div key={i} className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-6 space-y-1">
                              <Label className="text-[10px] text-slate-500">Cargo</Label>
                              <NativeSelect
                                value={it.cargo_id}
                                onValueChange={(v) => setCargoLinha(i, { cargo_id: v })}
                                placeholder="Selecione um cargo..."
                                options={[
                                  { value: "", label: "— Selecione —" },
                                  ...cargosComCusto.map((cc) => ({
                                    value: cc.id,
                                    label: `${cc.nome} — ${fmtBRL(cc.custo_diario)}/dia`,
                                  })),
                                ]}
                                data-testid={`equipe-cargo-${i}`}
                              />
                            </div>
                            <div className="col-span-3 space-y-1">
                              <Label className="text-[10px] text-slate-500">Quantidade</Label>
                              <Input
                                type="number" min="0"
                                value={it.quantidade}
                                onChange={(e) => setCargoLinha(i, { quantidade: e.target.value })}
                                data-testid={`equipe-qtd-${i}`}
                              />
                            </div>
                            <div className="col-span-2 space-y-1">
                              <Label className="text-[10px] text-slate-500">Custo Diário</Label>
                              <div className="h-9 px-2 flex items-center text-xs font-mono text-slate-600 bg-slate-50 rounded border">
                                {fmtBRL(c?.custo_diario || 0)}
                              </div>
                            </div>
                            <Button
                              type="button" variant="ghost" size="sm"
                              className="col-span-1 h-9 px-0 text-red-600"
                              onClick={() => removeCargoLinha(i)}
                              disabled={form.equipe_cargos.length <= 1}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                    <Button
                      type="button" variant="outline" size="sm" className="mt-2"
                      onClick={addCargoLinha}
                      data-testid="btn-add-cargo"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar Cargo
                    </Button>
                    <div className="mt-3 p-2 rounded bg-slate-50 text-xs flex items-center justify-between">
                      <span className="text-slate-600">
                        <b>{resumoEquipe.totalPessoas}</b> profissional(is)
                      </span>
                      <span className="font-mono">
                        Custo diário total: <b>{fmtBRL(resumoEquipe.custoDia)}</b>
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t">
                    <CardTitle className="text-base mb-2">3. Materiais & Multiplicadores</CardTitle>

                    {/* Lista de materiais do estoque */}
                    <div className="space-y-2 mb-3" data-testid="materiais-lista">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Materiais do estoque (preço auto-preenchido)</Label>
                        <span className="text-[11px] text-slate-500">
                          Soma: <b className="font-mono">{fmtBRL(materiaisSubtotal)}</b>
                        </span>
                      </div>
                      {(form.materiais_lista || []).length === 0 && (
                        <p className="text-[11px] text-slate-500 italic px-1">
                          Nenhum material da lista. Adicione abaixo, ou use o campo {'"Custo Material"'} para entrar valor manual.
                        </p>
                      )}
                      {(form.materiais_lista || []).map((linha, idx) => {
                        const mat = materiaisEstoque.find((m) => m.id === linha.material_id);
                        const preco = parseFloat(mat?.valor_unitario) || 0;
                        const qtd = parseFloat(linha.quantidade) || 0;
                        const sub = preco * qtd;
                        return (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`mat-linha-${idx}`}>
                            <div className="col-span-6">
                              <NativeSelect
                                value={linha.material_id}
                                onValueChange={(v) => {
                                  const novos = [...form.materiais_lista];
                                  novos[idx] = { ...novos[idx], material_id: v };
                                  setForm({ ...form, materiais_lista: novos });
                                }}
                                placeholder="Selecione material..."
                                options={[
                                  { value: "", label: "— Selecione —" },
                                  ...materiaisEstoque.map((m) => ({
                                    value: m.id,
                                    label: `${m.codigo} • ${m.nome}`,
                                  })),
                                ]}
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                type="number" min="0" step="0.01"
                                value={linha.quantidade}
                                placeholder="Qtde"
                                onChange={(e) => {
                                  const novos = [...form.materiais_lista];
                                  novos[idx] = { ...novos[idx], quantidade: e.target.value };
                                  setForm({ ...form, materiais_lista: novos });
                                }}
                                data-testid={`mat-qtd-${idx}`}
                              />
                            </div>
                            <div className="col-span-2 text-right text-xs text-slate-600 font-mono">
                              {fmtBRL(preco)}
                              {mat?.unidade?.sigla && <span className="text-slate-400">/{mat.unidade.sigla}</span>}
                            </div>
                            <div className="col-span-1 text-right text-xs font-semibold text-slate-800 font-mono">
                              {fmtBRL(sub)}
                            </div>
                            <div className="col-span-1 flex justify-end">
                              <Button
                                type="button" variant="ghost" size="sm"
                                onClick={() => {
                                  const novos = form.materiais_lista.filter((_, i) => i !== idx);
                                  setForm({ ...form, materiais_lista: novos });
                                }}
                                data-testid={`btn-remover-mat-${idx}`}
                              >
                                <X className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setForm({
                          ...form,
                          materiais_lista: [...(form.materiais_lista || []), emptyMaterialLinha()],
                        })}
                        data-testid="btn-add-material"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar material
                      </Button>
                    </div>

                    {(form.materiais_lista || []).length === 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Custo de Material — valor manual (R$)</Label>
                          <Input
                            type="number" min="0" step="0.01"
                            value={form.custo_material}
                            onChange={(e) => setForm({ ...form, custo_material: e.target.value })}
                            data-testid="input-material"
                          />
                          <p className="text-[10px] text-slate-500">
                            Use este campo apenas se preferir lançar um valor único sem detalhar materiais.
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      <MultInput
                        label="Markup MO"
                        value={form.multiplicador_mo}
                        onChange={(v) => setForm({ ...form, multiplicador_mo: v })}
                        testId="mult-mo"
                      />
                      <MultInput
                        label="Markup Material"
                        value={form.multiplicador_material}
                        onChange={(v) => setForm({ ...form, multiplicador_material: v })}
                        testId="mult-material"
                      />
                      <MultInput
                        label="Markup Custo Fixo"
                        value={form.multiplicador_custo_fixo}
                        onChange={(v) => setForm({ ...form, multiplicador_custo_fixo: v })}
                        testId="mult-cf"
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">
                      Multiplicadores entre 1,0 e 5,0. Ex: 2,0 = 100% de margem; 2,5 = 150% de margem.
                    </p>
                  </div>

                  <div className="pt-3 border-t">
                    <Label className="text-xs">Descrição / Observações</Label>
                    <Textarea
                      value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                      placeholder="Detalhes do escopo (opcional)..."
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-2 pt-3">
                    <Button
                      onClick={calcular}
                      disabled={calculando}
                      className="flex-1 bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
                      data-testid="btn-calcular"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${calculando ? "animate-spin" : ""}`} />
                      {calculando ? "Calculando..." : "Calcular"}
                    </Button>
                    <Button
                      onClick={salvar}
                      disabled={!calc || salvando}
                      variant="outline"
                      data-testid="btn-salvar-orc"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {salvando ? "Salvando..." : (editingId ? "Atualizar" : "Salvar")}
                    </Button>
                    {calc && (
                      <Button onClick={() => gerarPDF(calc, form)} variant="outline" data-testid="btn-pdf">
                        <FileDown className="h-4 w-4 mr-2" /> PDF
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RESULTADO */}
              <ResultadoCard calc={calc} form={form} />
            </div>
          </TabsContent>

          <TabsContent value="historico">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="relative mb-4">
                  <input
                    type="text"
                    placeholder="Buscar por cliente ou nº de proposta..."
                    value={searchHistorico}
                    onChange={(e) => setSearchHistorico(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-luvcorp-navy/30"
                    data-testid="input-search-historico"
                  />
                  <Sparkles className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                </div>
                {(() => {
                  const q = (searchHistorico || "").toLowerCase().trim();
                  const filtrados = !q ? orcamentos : orcamentos.filter((o) =>
                    (o.nome_cliente || "").toLowerCase().includes(q) ||
                    (o.numero_proposta || "").toLowerCase().includes(q) ||
                    String(o.numero || "").includes(q)
                  );
                  if (orcamentos.length === 0) {
                    return <p className="p-8 text-center text-slate-500 text-sm">Nenhum orçamento salvo ainda.</p>;
                  }
                  if (filtrados.length === 0) {
                    return <p className="p-8 text-center text-slate-500 text-sm">Nenhum resultado para {`"${q}"`}.</p>;
                  }
                  return (
                    <div className="divide-y divide-slate-100 -mx-4">
                      {filtrados.map((o) => (
                        <div
                          key={o.id}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
                          data-testid={`hist-${o.id}`}
                        >
                          <div className="h-10 w-10 rounded-full bg-luvcorp-navy/10 flex items-center justify-center text-luvcorp-navy font-bold flex-shrink-0">
                            #{o.numero}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">
                              {o.numero_proposta} — {o.nome_cliente}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(o.created_at).toLocaleString("pt-BR")}
                              {" · "}
                              Base {fmtBRL(o.result?.custos?.custo_base)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Preço de Venda</p>
                            <p className="text-lg font-bold text-emerald-700 font-mono">
                              {fmtBRL(o.result?.venda?.preco_venda)}
                            </p>
                          </div>
                          <Button
                            variant="ghost" size="sm" title="Visualizar"
                            onClick={() => visualizar(o.id)}
                            data-testid={`btn-hist-ver-${o.id}`}
                          >
                            <Sparkles className="h-4 w-4 text-luvcorp-navy" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" title="Gerar PDF"
                            onClick={() => gerarPDF(o.result, { nome_cliente: o.nome_cliente, numero_proposta: o.numero_proposta })}
                            data-testid={`btn-hist-pdf-${o.id}`}
                          >
                            <FileDown className="h-4 w-4 text-slate-600" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" title="Editar"
                            onClick={() => editar(o.id)}
                            data-testid={`btn-hist-edit-${o.id}`}
                          >
                            <Pencil className="h-4 w-4 text-slate-600" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" title="Excluir"
                            onClick={() => removerOrcamento(o.id)}
                            className="text-red-600"
                            data-testid={`btn-hist-del-${o.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* PdfView legado removido — agora o PDF abre em nova janela via window.open */}
      </div>

      <ConfigDialog
        open={showConfig}
        onOpenChange={setShowConfig}
        config={config}
        onSaved={loadConfig}
      />

      {/* ===== Dialog Visualizar Orçamento Salvo ===== */}
      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-luvcorp-navy" />
              {viewItem ? `Proposta #${viewItem.numero} — ${viewItem.numero_proposta}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {viewItem && <VisualizarOrcamento item={viewItem} />}
          </div>
          <DialogFooter className="px-6 py-3 border-t bg-white">
            <Button variant="outline" onClick={() => setViewItem(null)}>Fechar</Button>
            {viewItem && (
              <>
                <Button
                  variant="outline"
                  onClick={() => gerarPDF(viewItem.result, { nome_cliente: viewItem.nome_cliente, numero_proposta: viewItem.numero_proposta })}
                  data-testid="btn-view-pdf"
                >
                  <FileDown className="h-4 w-4 mr-2" /> Gerar PDF
                </Button>
                <Button
                  className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
                  onClick={() => { const id = viewItem.id; setViewItem(null); editar(id); }}
                  data-testid="btn-view-edit"
                >
                  <Pencil className="h-4 w-4 mr-2" /> Editar
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog PDF: visualização + ações de Abrir/Baixar (user-gesture seguro) ===== */}
      <Dialog open={pdfModal.open} onOpenChange={(o) => !o && closePdfModal()}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0" data-testid="dialog-pdf-preview">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5 text-luvcorp-navy" />
              PDF gerado — {pdfModal.filename}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Pré-visualização do PDF abaixo. Use os botões para abrir no visualizador nativo do navegador (Chrome, Edge, Safari, Adobe) ou baixar direto na pasta de Downloads ({getDownloadFolderHint()}).
            </p>
          </DialogHeader>
          <div className="flex-1 bg-slate-100 overflow-hidden">
            {pdfModal.blobUrl && (
              <iframe
                src={pdfModal.blobUrl}
                title="Pré-visualização do PDF"
                className="w-full h-full border-0"
                data-testid="iframe-pdf-preview"
              />
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t bg-white flex-row sm:justify-between gap-2">
            <Button variant="ghost" onClick={closePdfModal} data-testid="btn-pdf-close">
              Fechar
            </Button>
            <div className="flex gap-2">
              {pdfModal.blobUrl && (
                <>
                  <a
                    href={pdfModal.blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-slate-300 bg-white px-4 py-2 hover:bg-slate-50 transition-colors"
                    data-testid="btn-pdf-open-tab"
                  >
                    Abrir em nova aba
                  </a>
                  <a
                    href={pdfModal.blobUrl}
                    download={pdfModal.filename}
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-luvcorp-navy text-white px-4 py-2 hover:bg-luvcorp-navy/90 transition-colors"
                    data-testid="btn-pdf-download"
                  >
                    <FileDown className="h-4 w-4 mr-2" /> Baixar PDF
                  </a>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}

// =====================================================
function MultInput({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-slate-600">{label}</Label>
      <Input
        type="number" min="1" max="5" step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

// =====================================================
function ResultadoCard({ calc, form }) {
  if (!calc) {
    return (
      <Card className="border-0 shadow-sm border-dashed bg-slate-50/50">
        <CardContent className="p-8 text-center text-slate-500">
          <Calculator className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">
            Preencha os dados ao lado e clique em <b>Calcular</b>.
          </p>
        </CardContent>
      </Card>
    );
  }
  const { custos, venda, opcoes_pagamento, inputs } = calc;

  // Percentuais com fallback (compat com snapshots antigos sem pct_*)
  const pv = venda.preco_venda || 0;
  const pctMo  = venda.pct_mo_sobre_venda       ?? (pv > 0 ? +(venda.mo_com_markup       / pv * 100).toFixed(2) : 0);
  const pctCf  = venda.pct_cf_sobre_venda       ?? (pv > 0 ? +(venda.cf_com_markup       / pv * 100).toFixed(2) : 0);
  const pctMat = venda.pct_material_sobre_venda ?? (pv > 0 ? +(venda.material_com_markup / pv * 100).toFixed(2) : 0);

  // ===== Sensibilidade do Markup MO (client-side, sem nova chamada) =====
  // Mantemos Material e CF fixos com o markup atual; variamos somente o markup MO.
  const sensMOValues = [1.5, 1.75, 2.0, 2.25, 2.5, 2.75, 3.0];
  const cfBase = custos.cf_total * inputs.multiplicador_custo_fixo;
  const matBase = custos.custo_material * inputs.multiplicador_material;
  const sensRows = sensMOValues.map((m) => {
    const moComMk = custos.cmo_total * m;
    const preco = moComMk + cfBase + matBase;
    const lucro = preco - custos.custo_base;
    return { mult: m, preco, lucro, atual: Math.abs(m - inputs.multiplicador_mo) < 0.01 };
  });
  const maxPreco = Math.max(...sensRows.map((r) => r.preco));

  return (
    <Card className="border-0 shadow-sm" data-testid="resultado-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-emerald-600" />
          Resultado
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Linha label={`Mão de Obra (×${inputs.multiplicador_mo})`}
                 hint={`base: ${fmtBRL(custos.cmo_total)} · ${pctMo.toFixed(1)}% sobre venda`}
                 value={venda.mo_com_markup} />
          <Linha label={`Custo Fixo (×${inputs.multiplicador_custo_fixo})`}
                 hint={`base: ${fmtBRL(custos.cf_total)} · ${pctCf.toFixed(1)}% sobre venda`}
                 value={venda.cf_com_markup} />
          <Linha label={`Material (×${inputs.multiplicador_material})`}
                 hint={`base: ${fmtBRL(custos.custo_material)} · ${pctMat.toFixed(1)}% sobre venda`}
                 value={venda.material_com_markup} />
          <div className="border-t pt-2">
            <Linha label="Custo Base (sem markup)" value={custos.custo_base} muted />
          </div>
        </div>

        {/* Composição percentual da venda */}
        <div className="grid grid-cols-3 gap-2" data-testid="composicao-pct">
          <PctCard label="Mão de Obra" pct={pctMo} color="blue" />
          <PctCard label="Custo Fixo" pct={pctCf} color="amber" />
          <PctCard label="Material" pct={pctMat} color="emerald" />
        </div>

        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="card-preco-venda">
          <p className="text-xs text-emerald-700 font-medium">PREÇO DE VENDA</p>
          <p className="text-3xl font-bold text-emerald-800 font-mono mt-1">
            {fmtBRL(venda.preco_venda)}
          </p>
          <p className="text-[11px] text-emerald-700 mt-1">
            Lucro bruto {fmtBRL(venda.lucro_bruto)} ({venda.margem_pct_sobre_venda}% sobre venda)
          </p>
        </div>

        {/* ===== Sensibilidade do Markup MO ===== */}
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid="sensibilidade-mo">
          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            Sensibilidade do Markup MO
          </p>
          <div className="space-y-1">
            {sensRows.map((r) => (
              <div
                key={r.mult}
                className={`flex items-center gap-2 text-[11px] ${r.atual ? "font-bold text-slate-900" : "text-slate-600"}`}
              >
                <span className="w-10 font-mono">×{r.mult.toFixed(2)}</span>
                <div className="flex-1 h-3 bg-white rounded relative overflow-hidden border border-slate-200">
                  <div
                    className={`h-full ${r.atual ? "bg-emerald-500" : "bg-luvcorp-navy/50"}`}
                    style={{ width: `${(r.preco / maxPreco) * 100}%` }}
                  />
                </div>
                <span className="font-mono w-24 text-right">{fmtBRL(r.preco)}</span>
                <span className={`font-mono w-24 text-right ${r.lucro >= venda.lucro_bruto ? "text-emerald-700" : "text-amber-700"}`}>
                  +{fmtBRL(r.lucro)}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            Material e Custo Fixo fixos no markup atual. Linha em verde = configuração atual.
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            Opções de Pagamento (taxa juros {fmtPct(inputs.taxa_juros_mensal)} a.m.)
          </p>
          <div className="space-y-1 max-h-[26rem] overflow-y-auto pr-1">
            {opcoes_pagamento.map((op) => (
              <div
                key={op.id}
                className={`flex items-center gap-3 p-2 rounded border text-xs ${
                  op.com_juros
                    ? "border-amber-200 bg-amber-50/40"
                    : "border-emerald-200 bg-emerald-50/40"
                }`}
                data-testid={`opcao-${op.id}`}
              >
                <div className="h-6 w-6 rounded-full bg-luvcorp-navy/10 flex items-center justify-center text-luvcorp-navy font-bold text-[11px] flex-shrink-0">
                  {op.id}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{op.label}</p>
                  <p className="text-slate-600 text-[10px]">
                    Entrada {fmtBRL(op.valor_entrada)}
                    {op.num_parcelas > 0 && <> · {op.num_parcelas}× {fmtBRL(op.valor_parcela)}</>}
                    {op.com_juros && op.juros_total > 0 && (
                      <span className="ml-1 text-amber-700">· juros {fmtBRL(op.juros_total)}</span>
                    )}
                  </p>
                </div>
                <p className="font-mono text-[11px] text-slate-700">
                  Total {fmtBRL(op.total_pagar)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Linha({ label, value, hint, muted }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className={muted ? "text-slate-500" : "text-slate-700"}>{label}</span>
      <span className="text-right">
        <span className="font-mono font-semibold text-slate-800">{fmtBRL(value)}</span>
        {hint && <span className="block text-[10px] text-slate-400">{hint}</span>}
      </span>
    </div>
  );
}

function PctCard({ label, pct, color }) {
  const colors = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`rounded-md border p-2 text-center ${colors[color] || colors.blue}`}>
      <p className="text-[10px] font-medium uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-xl font-bold font-mono mt-0.5">{(pct || 0).toFixed(1)}%</p>
      <p className="text-[9px] opacity-60">sobre venda</p>
    </div>
  );
}

// =====================================================
// PDF VIEW (otimizado para print)
// =====================================================
function PdfView({ calc, form }) {
  const { custos, venda, opcoes_pagamento, inputs, cabecalho } = calc;
  return (
    <div className="p-8 text-black bg-white">
      <div className="border-b-2 border-slate-700 pb-3 mb-5">
        <h1 className="text-2xl font-bold">Proposta Comercial — Reforma de Fachada</h1>
        <div className="flex justify-between text-xs mt-2">
          <div>
            <p><b>Cliente:</b> {cabecalho?.nome_cliente || form.nome_cliente}</p>
            <p><b>Proposta:</b> {cabecalho?.numero_proposta || form.numero_proposta}</p>
          </div>
          <div className="text-right">
            <p><b>Emissão:</b> {new Date().toLocaleDateString("pt-BR")}</p>
          </div>
        </div>
      </div>

      <h2 className="text-sm font-bold mb-2">Equipe da Obra</h2>
      <table className="w-full text-xs border-collapse mb-4">
        <thead>
          <tr className="bg-slate-100">
            <th className="border p-1 text-left">Cargo</th>
            <th className="border p-1 text-center">Qtd</th>
            <th className="border p-1 text-right">Custo Diário</th>
            <th className="border p-1 text-right">Subtotal/dia</th>
          </tr>
        </thead>
        <tbody>
          {custos.cmo_breakdown.map((e, i) => (
            <tr key={i}>
              <td className="border p-1">{e.nome}</td>
              <td className="border p-1 text-center">{e.quantidade}</td>
              <td className="border p-1 text-right font-mono">{fmtBRL(e.custo_diario)}</td>
              <td className="border p-1 text-right font-mono">{fmtBRL(e.subtotal_dia)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="text-sm font-bold mb-2">Composição de Custos ({inputs.dias} dias × {inputs.num_obras} obras simultâneas)</h2>
      <table className="w-full text-xs border-collapse mb-4">
        <tbody>
          <tr><td className="border p-1">Mão de Obra Total (CMO)</td><td className="border p-1 text-right font-mono">{fmtBRL(custos.cmo_total)}</td><td className="border p-1 text-center">× {inputs.multiplicador_mo}</td><td className="border p-1 text-right font-mono">{fmtBRL(venda.mo_com_markup)}</td></tr>
          <tr><td className="border p-1">Custo Fixo (rateado)</td><td className="border p-1 text-right font-mono">{fmtBRL(custos.cf_total)}</td><td className="border p-1 text-center">× {inputs.multiplicador_custo_fixo}</td><td className="border p-1 text-right font-mono">{fmtBRL(venda.cf_com_markup)}</td></tr>
          <tr><td className="border p-1">Material</td><td className="border p-1 text-right font-mono">{fmtBRL(custos.custo_material)}</td><td className="border p-1 text-center">× {inputs.multiplicador_material}</td><td className="border p-1 text-right font-mono">{fmtBRL(venda.material_com_markup)}</td></tr>
          <tr className="bg-emerald-100"><td className="border p-1 font-bold">PREÇO DE VENDA</td><td className="border p-1 text-right">—</td><td className="border p-1"></td><td className="border p-1 text-right font-mono font-bold text-base">{fmtBRL(venda.preco_venda)}</td></tr>
        </tbody>
      </table>

      <h2 className="text-sm font-bold mb-2">Opções de Pagamento (juros {fmtPct(inputs.taxa_juros_mensal)} a.m. quando aplicável)</h2>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-100">
            <th className="border p-1">#</th>
            <th className="border p-1 text-left">Forma</th>
            <th className="border p-1 text-right">Entrada</th>
            <th className="border p-1 text-right">Parcelas</th>
            <th className="border p-1 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {opcoes_pagamento.map((op) => (
            <tr key={op.id}>
              <td className="border p-1 text-center">{op.id}</td>
              <td className="border p-1">{op.label}</td>
              <td className="border p-1 text-right font-mono">{fmtBRL(op.valor_entrada)}</td>
              <td className="border p-1 text-right font-mono">
                {op.num_parcelas > 0 ? `${op.num_parcelas}× ${fmtBRL(op.valor_parcela)}` : "—"}
              </td>
              <td className="border p-1 text-right font-mono">{fmtBRL(op.total_pagar)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-[10px] text-slate-500 mt-4">
        Obra Padrão · Sistema de Gestão de Obras — proposta gerada eletronicamente.
      </p>
    </div>
  );
}

// =====================================================
// Visualizar (read-only) Orçamento Salvo
// =====================================================
function VisualizarOrcamento({ item }) {
  const r = item.result || {};
  const c = r.custos || {};
  const v = r.venda || {};
  const inp = r.inputs || {};
  const ops = r.opcoes_pagamento || [];

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded border border-slate-200 text-sm">
        <div>
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Cliente</p>
          <p className="font-semibold">{item.nome_cliente}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Nº Proposta</p>
          <p className="font-semibold">{item.numero_proposta}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Criado em</p>
          <p>{new Date(item.created_at).toLocaleString("pt-BR")}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Duração / Obras</p>
          <p>{inp.dias} dias · {inp.num_obras} obras simult.</p>
        </div>
      </div>

      {/* Equipe */}
      <div>
        <h3 className="text-xs font-bold uppercase text-slate-600 mb-1.5">Equipe</h3>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-200 p-1.5 text-left">Cargo</th>
              <th className="border border-slate-200 p-1.5 text-center">Qtd</th>
              <th className="border border-slate-200 p-1.5 text-right">Custo Diário</th>
              <th className="border border-slate-200 p-1.5 text-right">Subtotal/dia</th>
            </tr>
          </thead>
          <tbody>
            {(c.cmo_breakdown || []).map((e, i) => (
              <tr key={i}>
                <td className="border border-slate-200 p-1.5">{e.nome}</td>
                <td className="border border-slate-200 p-1.5 text-center">{e.quantidade}</td>
                <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(e.custo_diario)}</td>
                <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(e.subtotal_dia)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Composição */}
      <div>
        <h3 className="text-xs font-bold uppercase text-slate-600 mb-1.5">Composição de Custos</h3>
        <table className="w-full text-xs border-collapse">
          <tbody>
            <tr>
              <td className="border border-slate-200 p-1.5">Mão de Obra (CMO)</td>
              <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(c.cmo_total)}</td>
              <td className="border border-slate-200 p-1.5 text-center">× {inp.multiplicador_mo}</td>
              <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(v.mo_com_markup)}</td>
            </tr>
            <tr>
              <td className="border border-slate-200 p-1.5">Custo Fixo (rateado)</td>
              <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(c.cf_total)}</td>
              <td className="border border-slate-200 p-1.5 text-center">× {inp.multiplicador_custo_fixo}</td>
              <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(v.cf_com_markup)}</td>
            </tr>
            <tr>
              <td className="border border-slate-200 p-1.5">Material</td>
              <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(c.custo_material)}</td>
              <td className="border border-slate-200 p-1.5 text-center">× {inp.multiplicador_material}</td>
              <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(v.material_com_markup)}</td>
            </tr>
            <tr className="bg-emerald-100 font-bold">
              <td className="border border-slate-200 p-1.5">PREÇO DE VENDA</td>
              <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(c.custo_base)}</td>
              <td className="border border-slate-200 p-1.5"></td>
              <td className="border border-slate-200 p-1.5 text-right font-mono text-base">{fmtBRL(v.preco_venda)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Opções */}
      <div>
        <h3 className="text-xs font-bold uppercase text-slate-600 mb-1.5">
          Opções de Pagamento (juros {((inp.taxa_juros_mensal || 0) * 100).toFixed(2)}% a.m.)
        </h3>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-200 p-1.5 text-center">#</th>
              <th className="border border-slate-200 p-1.5 text-left">Forma</th>
              <th className="border border-slate-200 p-1.5 text-right">Entrada</th>
              <th className="border border-slate-200 p-1.5 text-right">Parcelas</th>
              <th className="border border-slate-200 p-1.5 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {ops.map((op) => (
              <tr key={op.id} className={op.com_juros ? "bg-amber-50" : ""}>
                <td className="border border-slate-200 p-1.5 text-center">{op.id}</td>
                <td className="border border-slate-200 p-1.5">{op.label}</td>
                <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(op.valor_entrada)}</td>
                <td className="border border-slate-200 p-1.5 text-right font-mono">
                  {op.num_parcelas > 0 ? `${op.num_parcelas}× ${fmtBRL(op.valor_parcela)}` : "—"}
                </td>
                <td className="border border-slate-200 p-1.5 text-right font-mono">{fmtBRL(op.total_pagar)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================
// Config Dialog v2
// =====================================================
function ConfigDialog({ open, onOpenChange, config, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        custo_fixo_mensal: config.custo_fixo_mensal,
        dias_uteis_mes: config.dias_uteis_mes,
        obras_simultaneas: config.obras_simultaneas,
        taxa_juros_mensal: config.taxa_juros_mensal,
        opcoes_pagamento: [...(config.opcoes_pagamento || [])],
      });
    }
  }, [config, open]);

  if (!form) return null;

  const updateOpcao = (i, key, val) => {
    const arr = [...form.opcoes_pagamento];
    if (key === "com_juros") arr[i] = { ...arr[i], com_juros: val };
    else if (key === "label") arr[i] = { ...arr[i], label: val };
    else arr[i] = { ...arr[i], [key]: Number(val) || 0 };
    setForm({ ...form, opcoes_pagamento: arr });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/precificacao/config`, form);
      toast.success("Configurações atualizadas");
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" /> Configurações da Precificação
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Custo Fixo Mensal (R$)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.custo_fixo_mensal}
                onChange={(e) => setForm({ ...form, custo_fixo_mensal: Number(e.target.value) || 0 })}
                data-testid="cfg-custo-fixo"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Dias Úteis/Mês</Label>
              <Input
                type="number" min="1" max="31"
                value={form.dias_uteis_mes}
                onChange={(e) => setForm({ ...form, dias_uteis_mes: Number(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Obras Padrão</Label>
              <Input
                type="number" min="1"
                value={form.obras_simultaneas}
                onChange={(e) => setForm({ ...form, obras_simultaneas: Number(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Taxa de Juros (a.m.)</Label>
              <Input
                type="number" min="0" max="0.20" step="0.001"
                value={form.taxa_juros_mensal}
                onChange={(e) => setForm({ ...form, taxa_juros_mensal: Number(e.target.value) || 0 })}
                data-testid="cfg-taxa-juros"
              />
              <span className="text-[10px] text-slate-500">{(form.taxa_juros_mensal * 100).toFixed(2)}% ao mês</span>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold mb-2 block">Opções de Pagamento (8)</Label>
            <div className="space-y-2">
              {form.opcoes_pagamento.map((op, i) => (
                <div key={op.id} className="grid grid-cols-12 gap-2 items-center">
                  <span className="col-span-1 text-xs text-slate-400 text-center">#{op.id}</span>
                  <Input
                    className="col-span-4 h-8 text-xs"
                    value={op.label || ""}
                    onChange={(e) => updateOpcao(i, "label", e.target.value)}
                  />
                  <div className="col-span-3 flex items-center gap-1">
                    <Input className="h-8 text-xs" type="number" min="0" max="1" step="0.01"
                      value={op.percentual_entrada}
                      onChange={(e) => updateOpcao(i, "percentual_entrada", e.target.value)} />
                    <span className="text-[10px] text-slate-500">entr.</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    <Input className="h-8 text-xs" type="number" min="0"
                      value={op.num_parcelas}
                      onChange={(e) => updateOpcao(i, "num_parcelas", e.target.value)} />
                    <span className="text-[10px] text-slate-500">x</span>
                  </div>
                  <label className="col-span-2 flex items-center gap-1 text-[10px]">
                    <input
                      type="checkbox" checked={!!op.com_juros}
                      onChange={(e) => updateOpcao(i, "com_juros", e.target.checked)}
                    />
                    juros
                  </label>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              Entrada em decimal (0.30 = 30%). Marque {'"juros"'} para aplicar a taxa configurada.
            </p>
          </div>
        </div>
        <DialogFooter className="px-6 py-3 border-t bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={submit} disabled={saving}
            className="bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white"
          >
            {saving ? "Salvando..." : "Salvar Configurações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
